// Drawn three-quarter vehicles (default with the HD art; ?carview=0 turns them off). See docs/CAR_ART.md.
// Engine: a vehicle is a small coordinate model (client/src/car-models/<type>.ts) painted with flat polygons straight into
// pixels, once per heading (32 headings, baked lazily). Only visible faces and the wheels that can be seen are painted, so a
// side never shows two sets of wheels. Light from the top left; palette colours only. Not derived from the top-down canvas
// and not stacked slices. Types without a registered model return null from drawnCarTex (render.ts then uses the stacked art).
import { Texture } from 'pixi.js';
import type { VehicleType } from '../../common/src/data';
import { PAINTS, canvas, tex } from './art';
import { K, dk, lt } from './hd-kit';
import { outline } from './hd-sprites';

export const DRAWN_HEADINGS = 32;
export const CAR_CANVAS = { w: 64, h: 64, ox: 32, oy: 46 }; // ground centre of the vehicle sits at (ox, oy)
const W = CAR_CANVAS.w, H = CAR_CANVAS.h, OX = CAR_CANVAS.ox, OY = CAR_CANVAS.oy;

/** (u forward, v right of heading, z up) in hd px, origin at the ground centre. */
export type V3 = [number, number, number];
const LIGHT: V3 = [-0.55, -0.45, 0.7];

/** What a model's hooks can paint with. Everything is in model coordinates (u, v, z). */
export interface Painter {
  /** body paint colour (already the wrecked slate when wrecked) */
  body: string; wrecked: boolean; lampOn: boolean;
  /** the vehicle carries a gunner mount (kei open bed) */
  gunner: boolean;
  /** sin^2 of the heading: 0 seen from the side (E/W), 1 seen end-on (S/N); widen things that foreshorten at the ends */
  s2: number;
  /** like fill, but a polygon that rasterises to nothing still paints one pixel at its centre (slit windows) */
  fill1(pts: V3[], col: string): void;
  /** axle u positions after the end-view outward shift, and the wheel flare (extra half track at this heading) */
  axles: number[]; flare: number;
  fill(pts: V3[], col: string): void;
  line(pts: V3[], col: string, loop?: boolean): void;
  /** flat face colour from the light, and glass colour (dark when wrecked) */
  tone(n: V3): string; glassTone(n: V3): string;
  /** > 0 when a face with this body-frame normal faces the camera */
  toward(n: V3): number;
  /** a few bright crack pixels across a pane (only when wrecked) */
  cracks(pane: V3[]): void;
  endRect(u: number, v0: number, v1: number, z0: number, z1: number): V3[];
  sideRect(v: number, u0: number, u1: number, z0: number, z1: number): V3[];
  disc(u: number, v: number, z: number, r: number, n?: number): V3[];
  lerp3(a: V3, b: V3, t: number): V3;
  /** for `custom` models: queue a face (cabin = painted after the body faces), with decals drawn on top */
  face(pts: V3[], n: V3, col: string, decals?: () => void, cabin?: boolean): void;
}
export interface CabinFace { color?: string; decal?: () => void }

/** One vehicle type's shape and details. Lengths are hd px (1 hd px = half a world px). */
export interface CarModel {
  /** side profile (u forward, z up), counter-clockwise, extruded across +-hw. Edge 0 must be the bottom (never drawn). */
  body: [number, number][];
  hw: number;
  /** optional cabin profile on top of the body (edge 0 = its base, never drawn), extruded across +-hwc */
  cabin?: [number, number][];
  hwc?: number;
  /** body/cabin profiles used when wrecked (same number of edges as the intact ones, so the edge hooks keep their indices) */
  wreckedBody?: [number, number][];
  wreckedCabin?: [number, number][];
  /** tyre width along v (default 3.2) */
  wheelW?: number;
  /** axle positions along u (front positive, rear negative); the engine adds a small outward shift in end views */
  axles: number[];
  wheelR: number;
  /** top of the wheel arch ring in z (default 8.6) */
  archTop?: number;
  /** body colour for a paint index (default PAINTS[paint]); wrecked is always dark slate */
  bodyColor?(paint: number): string;
  /** decal painter for body profile edge i (the bumpers, lamps, hood marks); edge 0 is skipped */
  bodyEdge?(i: number, P: Painter): (() => void) | undefined;
  /** decals for the side wall at v = s * hw (door seams, handles, livery); sill and wheel arches are drawn first by the engine */
  bodySide?(s: number, v: number, P: Painter): (() => void) | undefined;
  /** colour and decal for cabin edge i (windscreen, roof, rear window); `at(t, v)` is a point along the edge */
  cabinEdge?(i: number, P: Painter, g: { n: V3; at: (t: number, v: number) => V3 }): CabinFace | undefined;
  /** decals for the cabin side wall at v = s * hwc (side windows) */
  cabinSide?(s: number, v: number, P: Painter): (() => void) | undefined;
  /** Full control: replaces the default body/cabin faces entirely (a bike, an open-bed truck). Queue faces with P.face and paint
   *  with P.fill; the engine still draws the wheels (unless noWheels), rounds the corners and outlines. */
  custom?(P: Painter): void;
  /** the model paints its own wheels (with `custom`) */
  noWheels?: boolean;
}

const models = new Map<VehicleType, CarModel>();
export function registerCarModel(type: VehicleType, model: CarModel) { models.set(type, model); }
export const hasCarModel = (type: VehicleType) => models.has(type);

export function drawCarCanvas(model: CarModel, paint: number, idx: number, wrecked = false, gunner = false): HTMLCanvasElement {
  const BODY = wrecked && model.wreckedBody ? model.wreckedBody : model.body, CABIN = wrecked && model.wreckedCabin ? model.wreckedCabin : model.cabin, HW = model.hw, HWC = model.hwc ?? model.hw, WHEEL_R = model.wheelR;
  const body = wrecked ? dk(K.slate, .52) : model.bodyColor ? model.bodyColor(paint) : PAINTS[((paint % PAINTS.length) + PAINTS.length) % PAINTS.length];
  const th = idx * Math.PI * 2 / DRAWN_HEADINGS, ct = Math.cos(th), st = Math.sin(th), s2 = st * st;
  // end views look more frontal: less ground squash, taller walls (side views keep 0.72 / 1.05)
  const TILT = 0.72 - 0.1 * Math.abs(st), ZS = 1.05 + 0.25 * Math.abs(st);
  const AXLES = model.axles.map(u => u + Math.sign(u) * 3 * s2), FLARE = 2.6 + 1.6 * s2; // axles move toward the ends, track widens, in end views
  // read back once for the corner rounding: ask for a CPU canvas, or the GPU readback stalls the frame (hundreds of ms in a live page)
  const c = document.createElement('canvas'); c.width = W; c.height = H; const g = c.getContext('2d', { willReadFrequently: true })!;
  const proj = (p: V3): [number, number] => [OX + p[0] * ct - p[1] * st, OY + TILT * (p[0] * st + p[1] * ct) - ZS * p[2]];
  const world = (n: V3): V3 => [n[0] * ct - n[1] * st, n[0] * st + n[1] * ct, n[2]];
  const toward = (n: V3) => { const w = world(n); return w[1] * ZS + w[2] * TILT; }; // > 0: faces the camera
  const fill = (pts: V3[], col: string): number => {
    const s = pts.map(proj); g.fillStyle = col; let count = 0;
    const y0 = Math.ceil(Math.min(...s.map(p => p[1])) - .5), y1 = Math.floor(Math.max(...s.map(p => p[1])) - .5);
    for (let y = y0; y <= y1; y++) {
      const cuts: number[] = [];
      for (let i = 0; i < s.length; i++) { const a = s[i], b = s[(i + 1) % s.length]; if ((a[1] <= y + .5 && b[1] > y + .5) || (b[1] <= y + .5 && a[1] > y + .5)) cuts.push(a[0] + (y + .5 - a[1]) / (b[1] - a[1]) * (b[0] - a[0])); }
      cuts.sort((p, q) => p - q);
      for (let i = 0; i + 1 < cuts.length; i += 2) { const x0 = Math.round(cuts[i]), x1 = Math.round(cuts[i + 1]); if (x1 > x0) { g.fillRect(x0, y, x1 - x0, 1); count += x1 - x0; } }
    }
    return count;
  };
  const fill1 = (pts: V3[], col: string) => { if (fill(pts, col) === 0) { const s = pts.map(proj), cx = s.reduce((a, p) => a + p[0], 0) / s.length, cy = s.reduce((a, p) => a + p[1], 0) / s.length; g.fillStyle = col; g.fillRect(Math.floor(cx), Math.floor(cy), 1, 1); } };
  /** 1 px line along a polyline (closed when `loop`) */
  const line = (pts: V3[], col: string, loop = true) => {
    const s = pts.map(proj); g.fillStyle = col;
    for (let i = 0; i < s.length - (loop ? 0 : 1); i++) {
      const a = s[i], b = s[(i + 1) % s.length]; let x0 = Math.floor(a[0]), y0 = Math.floor(a[1]); const x1 = Math.floor(b[0]), y1 = Math.floor(b[1]);
      const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1; let e = dx + dy;
      for (;;) { g.fillRect(x0, y0, 1, 1); if (x0 === x1 && y0 === y1) break; const e2 = 2 * e; if (e2 >= dy) { e += dy; x0 += sx; } if (e2 <= dx) { e += dx; y0 += sy; } }
    }
  };
  const lam = (n: V3) => { const w = world(n); return .5 + .5 * (w[0] * LIGHT[0] + w[1] * LIGHT[1] + w[2] * LIGHT[2]); };
  const tone = (n: V3) => { const l = lam(n); return l > .7 ? lt(body, .22) : l > .45 ? body : l > .2 ? dk(body, .25) : dk(body, .45); };
  const glassTone = (n: V3) => { if (wrecked) return K.glassLo; const l = lam(n); return l > .6 ? K.glassHi : l > .3 ? K.glass : K.glassLo; };
  const lerp3 = (a: V3, b: V3, t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  /** broken glass on a pane (corners p0..p3): irregular lighter shards in one corner and a dark hole, never symmetric marks */
  const cracks = (p: V3[]) => {
    if (!wrecked) return;
    const pt = (a: number, b: number): V3 => [p[0][0] + a * (p[3][0] - p[0][0]) + b * (p[1][0] - p[0][0]), p[0][1] + a * (p[3][1] - p[0][1]) + b * (p[1][1] - p[0][1]), p[0][2] + a * (p[3][2] - p[0][2]) + b * (p[1][2] - p[0][2])];
    fill([pt(.04, .06), pt(.3, .1), pt(.14, .42)], K.glass); fill([pt(.34, .12), pt(.5, .3), pt(.28, .34)], K.glassHi); fill([pt(.06, .5), pt(.2, .62), pt(.08, .8)], K.glassHi);
    fill([pt(.56, .52), pt(.76, .46), pt(.84, .72), pt(.62, .84)], K.ink); fill([pt(.84, .72), pt(.92, .66), pt(.9, .86)], K.glass);
  };
  const endRect = (u: number, v0: number, v1: number, z0: number, z1: number): V3[] => [[u, v0, z0], [u, v1, z0], [u, v1, z1], [u, v0, z1]];
  const sideRect = (v: number, u0: number, u1: number, z0: number, z1: number): V3[] => [[u0, v, z0], [u1, v, z0], [u1, v, z1], [u0, v, z1]];
  const disc = (u: number, v: number, z: number, r: number, n = 14): V3[] => Array.from({ length: n }, (_, i) => [u + Math.cos(i / n * Math.PI * 2) * r, v, z + Math.sin(i / n * Math.PI * 2) * r] as V3);
  const sideVis = (s: number) => s * ct > .2;
  const endVis = (e: number) => e * st > .2; // e = +1 front (u+), -1 rear

  type Item = { depth: number; draw: () => void };
  const items: Item[] = [], cabinItems: Item[] = [];
  const depthOf = (pts: V3[]) => { let d = 0; for (const p of pts) { const w = world(p); d += w[1] * ZS + w[2] * TILT; } return d / pts.length; };
  const face = (list: Item[], pts: V3[], n: V3, col: string, decals?: () => void) => {
    if (toward(n) <= 0) return; list.push({ depth: depthOf(pts), draw: () => { fill(pts, col); decals?.(); line(pts, wrecked ? lt(col, .3) : dk(col, .22)); } }); // mid-tone creases; on a wreck they are lit edges of bent metal
  };
  const wheel = (u: number, s: number, rim: boolean) => {
    const zc = WHEEL_R, vout = HW + FLARE, ww = (model.wheelW ?? 3.2) + 1.2 * s2 /* wider tread at the ends so the corner tyres read ~4 px */;
    const din = disc(u, s * (vout - ww), zc, WHEEL_R, 18), dout = disc(u, s * vout, zc, WHEEL_R, 18);
    fill(din, K.tarLo);
    for (let j = 0; j < din.length; j++) { const k = (j + 1) % din.length; fill([din[j], din[k], dout[k], dout[j]], j % 4 === 0 ? K.tar : K.tarLo); } // subtle tread
    fill(dout, K.tarLo);
    if (!rim) return;
    const v = s * (vout + .2);
    fill(disc(u, v, zc, WHEEL_R, 18), K.ink); fill(disc(u, v, zc, WHEEL_R - .6, 18), K.tarLo);
    fill(disc(u, v, zc, WHEEL_R - 1.5, 18), K.metalLo); fill(disc(u, v, zc, WHEEL_R - 2.6, 18), K.metalHi); fill(disc(u, v, zc, 1, 8), K.metalLo); // rim and a centred hub
  };
  const P: Painter = {
    body, wrecked, gunner, lampOn: !wrecked, axles: AXLES, flare: FLARE, s2,
    fill, fill1, line, tone, glassTone, toward, cracks, endRect, sideRect, disc, lerp3,
    face: (pts, n, col, decals, cabin) => face(cabin ? cabinItems : items, pts, n, col, decals),
  };

  // far wheels that peek out at the near end of the car (drawn first, the body covers most of them)
  if (!model.noWheels) for (const u of AXLES) for (const s of [1, -1]) {
    if (sideVis(s)) continue;
    if (endVis(u > 0 ? 1 : -1)) wheel(u, s, false);
  }

  if (model.custom) model.custom(P);
  else {
    // body: one face per profile edge, plus the two side caps
    for (let i = 0; i < BODY.length; i++) {
      const a = BODY[i], b = BODY[(i + 1) % BODY.length], du = b[0] - a[0], dz = b[1] - a[1], l = Math.hypot(du, dz);
      const n: V3 = [dz / l, 0, -du / l];
      const pts: V3[] = [[a[0], -HW, a[1]], [b[0], -HW, b[1]], [b[0], HW, b[1]], [a[0], HW, a[1]]];
      if (i === 0) continue;
      face(items, pts, n, n[2] > .8 ? body : tone(n), model.bodyEdge?.(i, P));
    }
    for (const s of [1, -1]) {
      const n: V3 = [0, s, 0], v = s * HW;
      face(items, BODY.map(p => [p[0], v, p[1]] as V3), n, tone(n), () => {
        fill(sideRect(v, -22, 22, 3, 4.2), K.metalLo); // sill
        for (const u of AXLES) fill(disc(u, s * (HW + FLARE), WHEEL_R, WHEEL_R + .9).map(p => [p[0], p[1], Math.min(model.archTop ?? 8.6, Math.max(3, p[2]))] as V3), K.ink); // arches
        model.bodySide?.(s, v, P)?.();
      });
    }
    if (CABIN) {
      for (let i = 1; i < CABIN.length; i++) {
        const a = CABIN[i], b = CABIN[(i + 1) % CABIN.length], du = b[0] - a[0], dz = b[1] - a[1], l = Math.hypot(du, dz);
        const n: V3 = [dz / l, 0, -du / l];
        const at = (t: number, v: number): V3 => [a[0] + du * t, v, a[1] + dz * t];
        const pts: V3[] = [[a[0], -HWC, a[1]], [b[0], -HWC, b[1]], [b[0], HWC, b[1]], [a[0], HWC, a[1]]];
        const cf = model.cabinEdge?.(i, P, { n, at });
        face(cabinItems, pts, n, cf?.color ?? tone(n), cf?.decal);
      }
      for (const s of [1, -1]) {
        const n: V3 = [0, s, 0], v = s * HWC, cap = CABIN.map(p => [p[0], v, p[1]] as V3);
        face(cabinItems, cap, n, tone(n), model.cabinSide?.(s, v, P));
      }
    }
  }
  items.sort((a, b) => a.depth - b.depth); cabinItems.sort((a, b) => a.depth - b.depth);
  for (const it of items) it.draw();
  for (const it of cabinItems) it.draw();
  // wheels on the visible side, sitting in their arches
  if (!model.noWheels) for (const s of [1, -1]) if (sideVis(s)) for (const u of AXLES) wheel(u, s, true);
  // round the silhouette: knock out 90 degree outer corners (two straight pixels each way) by one pixel
  const im = g.getImageData(0, 0, W, H), a = im.data, on = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && a[(y * W + x) * 4 + 3] > 0;
  const kill: number[] = [];
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (!on(x, y)) continue;
    for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      if (!on(x + sx, y) && !on(x, y + sy) && on(x - sx, y) && on(x - 2 * sx, y) && on(x, y - sy) && on(x, y - 2 * sy) && !on(x - sx, y + sy) && !on(x + sx, y - sy)) { kill.push(x, y); break; }
    }
  }
  for (let i = 0; i < kill.length; i += 2) g.clearRect(kill[i], kill[i + 1], 1, 1);
  return outline(c);
}

/** The baked canvas for a registered type, or null when it has no drawn model. */
export function drawnCarCanvas(type: VehicleType, paint: number, idx: number, wrecked = false, gunner = false): HTMLCanvasElement | null {
  const m = models.get(type); return m ? drawCarCanvas(m, paint, idx, wrecked, gunner) : null;
}

const cache = new Map<string, Texture>();
/** Idle-time warm-up: when a type/paint first shows, its other headings are queued and baked a few per frame (warmCars). */
export const carWarmOn = typeof location === 'undefined' || new URLSearchParams(location.search).get('carwarm') !== '0';
const warmQueue: { type: VehicleType; pc: number; idx: number; wrecked: boolean; g: boolean }[] = [], warmSeen = new Set<string>();
/** Bake queued headings until `budgetMs` is spent (always at least one when the queue is not empty). Call once per frame. */
export function warmCars(budgetMs = 2) {
  const t0 = performance.now();
  while (warmQueue.length) {
    const w = warmQueue.shift()!, m = models.get(w.type); if (!m) continue;
    const key = `${w.type}:${w.pc}:${w.idx}:${w.wrecked}:${w.g}`; if (cache.has(key)) continue;
    const b0 = performance.now(); cache.set(key, tex(drawCarCanvas(m, w.pc, w.idx, w.wrecked, w.g))); bakeStats.count++; bakeStats.warm++; bakeStats.ms += performance.now() - b0;
    if (performance.now() - t0 >= budgetMs) break;
  }
  bakeStats.maxWarmFrameMs = Math.max(bakeStats.maxWarmFrameMs, performance.now() - t0);
}
export const drawnIdx = (a: number) => ((Math.round(a / (Math.PI * 2) * DRAWN_HEADINGS) % DRAWN_HEADINGS) + DRAWN_HEADINGS) % DRAWN_HEADINGS;
export const bakeStats = { count: 0, ms: 0, inline: 0, warm: 0, maxInlineMs: 0, maxWarmFrameMs: 0 };
if (typeof window !== 'undefined') (window as unknown as { __carBake: typeof bakeStats }).__carBake = bakeStats; // read by the review scripts
/** Texture for a drawn vehicle at heading `a` (radians); ground centre at anchor (0.5, anchorY). Null when `type` has no model. */
export function drawnCarTex(type: VehicleType, paint: number, a: number, wrecked = false, gunner = false): { tex: Texture; anchorY: number } | null {
  const m = models.get(type); if (!m) return null;
  const pc = ((paint % PAINTS.length) + PAINTS.length) % PAINTS.length, i = drawnIdx(a), g = type === 'kei' && gunner, key = `${type}:${pc}:${i}:${wrecked}:${g}`;
  let t = cache.get(key);
  if (carWarmOn) { const wk = `${type}:${pc}:${wrecked}:${g}`; if (!warmSeen.has(wk)) { warmSeen.add(wk); for (let k = 1; k <= DRAWN_HEADINGS / 2; k++) for (const d of k === DRAWN_HEADINGS / 2 ? [k] : [k, -k]) warmQueue.push({ type, pc, idx: (i + d + DRAWN_HEADINGS) % DRAWN_HEADINGS, wrecked, g }); } } // nearest headings first (a turning car needs its neighbours)
  if (!t) { const t0 = performance.now(); t = tex(drawCarCanvas(m, pc, i, wrecked, g)); bakeStats.count++; bakeStats.inline++; bakeStats.ms += performance.now() - t0; bakeStats.maxInlineMs = Math.max(bakeStats.maxInlineMs, performance.now() - t0); cache.set(key, t); }
  return { tex: t, anchorY: OY / H };
}
